import { AVAILABLE } from "@/constants/area";
import { AREA_COLORS, BADGE_COLOR } from "@/constants/colors";
import { ASSIGNED, RELEASED, RELEASE_PENDING } from "@/constants/request";
import { SUBCONTRACTOR } from "@/constants/roles";
import { ACCEPTED, OVERDUE, REJECTED } from "@/constants/tasks";
import { IArea } from "@/interfaces/level.interface";
import { RootState } from "@/store/store";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

interface CanvasEditorProps {
  imageUrl: string;
  areas: IArea[];
  onAreaClick: (areaId: string) => void;
  onAreasChange: (areas: IArea[]) => void;
  isDrawMode: boolean;
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  fitScreen: boolean;
  setFitScreen: React.Dispatch<React.SetStateAction<boolean>>;
  drawLabels: boolean;
  shapeType: string;
  selectedCompanyId?: string | null;
  selectedAarStatus?: string | null;
  selectedActivityValue?: string | null;
}

// Helper to map shapeType to number of sides
function getMaxPoints(shapeType: string): number {
  switch (shapeType) {
    case "square":
      return 4;
    case "hexagon":
      return 6;
    case "octagon":
      return 8;
    case "decagon":
      return 16;
    default:
      return 4; // fallback to square
  }
}

function usePreventTrackpadZoom() {
  useEffect(() => {
    const wheelHandler = (e: WheelEvent) => {
      // Only block if ctrlKey is pressed (this means pinch-zoom on most laptops/trackpads)
      // But do not block if it's a keyboard event
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      window.removeEventListener("wheel", wheelHandler);
    };
  }, []);
}

function CanvasEditor({
  imageUrl,
  areas,
  onAreaClick,
  onAreasChange,
  isDrawMode,
  scale,
  setScale,
  fitScreen,
  setFitScreen,
  drawLabels,
  shapeType,
  selectedCompanyId,
  selectedAarStatus,
  selectedActivityValue,
}: CanvasEditorProps) {
  usePreventTrackpadZoom();
  const { user } = useSelector((state: RootState) => state.user);

  const canvasRef = useRef<
    HTMLCanvasElement & { currentScaleX: any; currentScaleY: any }
  >(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [image] = useState(new Image());
  const [drawingPoints, setDrawingPoints] = useState<
    Array<{ x: number; y: number }>
  >([]);
  const [currentMousePos, setCurrentMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizingShape, setResizingShape] = useState<any>(null);
  const [resizingDotIndex, setResizingDotIndex] = useState<number | null>(null);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [isZooming, setIsZooming] = useState(false);

  // Update image source when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      image.src = imageUrl;
      image.onload = () => {
        fitToScreen();
      };
    }
  }, [imageUrl]);

  useEffect(() => {
    const handleResize = () => {
      fitToScreen();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (fitScreen) {
      fitToScreen();
      setFitScreen(false);
    }
  }, [fitScreen]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grab";
        }
        setDrawingPoints([]);
        setCurrentMousePos(null);
        setIsDragging(false);
        setIsResizing(false);
        setResizingShape(null);
        setResizingDotIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  useEffect(() => {
    draw();
  }, [
    scale,
    offset,
    drawingPoints,
    currentMousePos,
    areas,
    isDrawMode,
    drawLabels,
  ]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image.complete) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    areas.forEach((area) => {
      if (area.isActive) {
        drawShape(ctx, area);

        // draw badge for active requests
        // draw badge at centroid
        if (!drawLabels && area.activeRequestCount! > 0) {
          // 1) get polygon centroid
          const { x: cx, y: cy } = computeCentroid(area.coordinates);

          // 2) radius = 10% of smaller bbox dimension, clamped [12..24]
          const xs = area.coordinates.map((p) => p.x);
          const ys = area.coordinates.map((p) => p.y);
          const boxW = Math.max(...xs) - Math.min(...xs);
          const boxH = Math.max(...ys) - Math.min(...ys);
          const r = Math.max(14, Math.min(28, Math.min(boxW, boxH) * 0.1));

          // 3) draw red circle
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fillStyle = BADGE_COLOR;
          ctx.fill();

          // 4) draw white text
          ctx.fillStyle = "white";
          ctx.font = `bold ${r * 0.8}px sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(area.activeRequestCount), cx, cy);
        }

        if (drawLabels && area.name) {
          // 1) Compute centroid
          const { x: cx, y: cy } = computeCentroid(area.coordinates);
          // 2) Font sizing & measurement
          const fontSize = calculateFontSize();
          ctx.font = `bold ${fontSize}px 'Segoe UI', sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // 3) Bounding box of the shape
          const xs = area.coordinates.map((p) => p.x);
          const ys = area.coordinates.map((p) => p.y);
          const boxWidth = Math.max(...xs) - Math.min(...xs);
          const boxHeight = Math.max(...ys) - Math.min(...ys);

          // 4) Wrap text into lines
          const maxTextWidth = boxWidth * 0.8;
          let lines = wrapText(ctx, area.name, maxTextWidth);

          // 5) Determine max lines by boxHeight and lineHeight
          const lineHeight = fontSize * 1.2;
          const maxLines = Math.floor((boxHeight * 0.8) / lineHeight);

          // 6) If we have too many, truncate the last line with “…”
          if (lines.length > maxLines) {
            lines = lines.slice(0, maxLines);
            let last = lines[maxLines - 1];
            // shorten last until it fits
            // add last undefined check
            if (last === undefined) {
              last = "";
            }

            while (
              ctx.measureText(last + "…").width > maxTextWidth &&
              last.length > 0
            ) {
              last = last.slice(0, -1);
            }
            lines[maxLines - 1] = last + "…";
          }

          // 7) Background rectangle
          const textBlockHeight = lines.length * lineHeight;
          const textWidths = lines.map((l) => ctx.measureText(l).width);
          const textWidth = Math.max(...textWidths);
          const padding = Math.max(3, fontSize / 5);
          const rectX = cx - textWidth / 2 - padding;
          const rectY = cy - textBlockHeight / 2 - padding;
          ctx.fillStyle = "rgba(255,255,255,0.8)";
          ctx.strokeStyle = "rgba(224,18,18,0.5)";
          ctx.lineWidth = 1;
          ctx.fillRect(
            rectX,
            rectY,
            textWidth + padding * 2,
            textBlockHeight + padding * 2
          );
          ctx.strokeRect(
            rectX,
            rectY,
            textWidth + padding * 2,
            textBlockHeight + padding * 2
          );

          // 8) Draw each line
          ctx.fillStyle = "black";
          lines.forEach((line, i) => {
            const y =
              cy - textBlockHeight / 2 + i * lineHeight + lineHeight / 2;
            ctx.fillText(line, cx, y);
          });
        }
      }
    });

    // Draw current drawing points
    if (drawingPoints.length > 0) {
      drawPreview(ctx);
    }

    ctx.restore();
  };

  const calculateFontSize = () => {
    // Base the font size on image dimensions and current scale
    const baseSize = (Math.max(image.width, image.height) / 100) * (1 / scale);

    // Add a minimum size to prevent tiny text
    const minSize = 15;

    // Add a maximum size to prevent enormous text
    const maxSize = 30;

    return Math.min(Math.max(baseSize, minSize), maxSize);
  };

  const getColors = (area: IArea) => {
    // When a company or activity filter is active, color by matching request status
    if (selectedCompanyId || selectedActivityValue) {
      let companyRequests = area?.allRequests ?? [];

      // Filter by company
      if (selectedCompanyId) {
        companyRequests = companyRequests.filter((r) =>
          r.companies?.some((c: any) => c.id === selectedCompanyId)
        );
      }

      // Filter by activity
      if (selectedActivityValue) {
        if (selectedCompanyId) {
          // Activity must belong to the selected company within the request
          companyRequests = companyRequests.filter((r: any) => {
            const company = r.companies?.find(
              (c: any) => c.id === selectedCompanyId
            );
            return company?.activities?.some(
              (a: any) => a.name === selectedActivityValue
            );
          });
        } else {
          // No company filter — activity can be in any company
          companyRequests = companyRequests.filter((r: any) =>
            r.companies?.some((c: any) =>
              c.activities?.some((a: any) => a.name === selectedActivityValue)
            )
          );
        }
      }

      // When AAR status is also selected, narrow to only those matching requests
      if (selectedAarStatus) {
        companyRequests = companyRequests.filter(
          (r: any) => r.status === selectedAarStatus
        );
      }

      if (companyRequests.length === 0) {
        return AREA_COLORS.white; // No matching AAR for this company / activity / status
      }

      // Priority: Orange > Red > Green
      if (
        companyRequests.some(
          (r: any) => r.status === ASSIGNED || r.status === ACCEPTED || r.status === OVERDUE
        )
      ) {
        return AREA_COLORS.orange;
      }

      if (companyRequests.some((r: any) => r.status === REJECTED)) {
        return AREA_COLORS.red;
      }

      if (
        companyRequests.some(
          (r: any) => r.status === RELEASED || r.status === RELEASE_PENDING
        )
      ) {
        return AREA_COLORS.green;
      }

      // Fallback white for any other status (e.g. Overdue)
      return AREA_COLORS.white;
    }

    // const hasRejected = area?.allRequests?.some((r) => r.status === REJECTED);
    // if (hasRejected) {
    //   return AREA_COLORS.red;
    // }

    if (user?.roles.includes(SUBCONTRACTOR) && area?.status === ASSIGNED) {
      //If assigned to him then show orange
      for (let aUser of area?.assignedUsers!) {
        if (aUser?.userId === user?.id) {
          return AREA_COLORS.orange;
        }
      }

      //If not assigned to him then show blue
      return AREA_COLORS.blue;
    }

    //For Released and available show green
    if (area?.status === RELEASED || area?.status === AVAILABLE) {
      return AREA_COLORS.green;
    }

    //For roles other than subcontractor
    if (area?.status === ASSIGNED) {
      return AREA_COLORS.orange;
    }

    return AREA_COLORS.blue;
  };

  const drawShape = (ctx: CanvasRenderingContext2D, area: IArea) => {
    if (!area.coordinates || area.coordinates.length < 3) return;
    const { fill, stroke } = getColors(area);

    ctx.beginPath();
    ctx.moveTo(area.coordinates[0].x, area.coordinates[0].y);
    area.coordinates.forEach((point: any, i: number) => {
      if (i > 0) ctx.lineTo(point.x, point.y);
    });
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = stroke;
    ctx.stroke();

    if (isDrawMode && area.status === AVAILABLE) {
      drawShapeDots(ctx, area);
      // Draw label only if showTitles is true
    }
  };

  const drawPreview = (ctx: CanvasRenderingContext2D) => {
    if (drawingPoints.length === 0) return;

    ctx.beginPath();
    ctx.moveTo(drawingPoints[0].x, drawingPoints[0].y);

    // Draw lines between existing points
    drawingPoints.forEach((point, i) => {
      if (i > 0) ctx.lineTo(point.x, point.y);
    });

    // Draw preview line to current mouse position
    if (currentMousePos) {
      const maxPoints = getMaxPoints(shapeType);
      if (drawingPoints.length < maxPoints) {
        ctx.lineTo(currentMousePos.x, currentMousePos.y);
      }

      // Close path when we have enough points or when showing preview of final point
      if (
        drawingPoints.length >= 3 &&
        (drawingPoints.length === maxPoints - 1 ||
          drawingPoints.length === maxPoints)
      ) {
        ctx.closePath();
      }
    }

    // Always close path if we have the complete shape
    const maxPoints = getMaxPoints(shapeType);
    if (drawingPoints.length === maxPoints) {
      ctx.closePath();
    }

    ctx.fillStyle = "rgba(0, 128, 0, 0.3)";
    ctx.strokeStyle = "rgba(0, 128, 0, 0.8)";
    ctx.stroke();
    ctx.fill();

    // Draw points
    drawingPoints.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx.fillStyle = "white";
      ctx.strokeStyle = "black";
      ctx.fill();
      ctx.stroke();
    });
  };

  const drawShapeDots = (ctx: CanvasRenderingContext2D, area: IArea) => {
    const points = getPolygonDotPositions(area.coordinates);
    ctx.lineWidth = 2;
    points.forEach((point) => {
      ctx.fillStyle = "white";
      ctx.strokeStyle = "black";
      ctx.beginPath();
      ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    });
    ctx.lineWidth = 1;
  };

  const getPolygonDotPositions = (points: Array<{ x: number; y: number }>) => {
    // const dotPositions = [];

    // for (let i = 0; i < points.length; i++) {
    //   const currentPoint = points[i];
    //   const nextPoint = points[(i + 1) % points.length];

    //   dotPositions.push(currentPoint);
    //   dotPositions.push({
    //     x: (currentPoint.x + nextPoint.x) / 2,
    //     y: (currentPoint.y + nextPoint.y) / 2,
    //   });
    // }
    // return dotPositions;
    // Only return the actual corner points, no midpoints
    return points;
  };

  const fitToScreen = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !image.complete) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    const padding = 10;
    const scaleX = (canvas.width - padding * 2) / image.width;
    const scaleY = (canvas.height - padding * 2) / image.height;
    const newScale = Math.min(scaleX, scaleY);

    canvas.currentScaleX = newScale;
    canvas.currentScaleY = newScale;

    const newOffsetX = canvas.width / 2;
    const newOffsetY = canvas.height / 2;

    setScale(newScale);
    setOffset({ x: newOffsetX, y: newOffsetY });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    const x = (pos.x - offset.x) / scale;
    const y = (pos.y - offset.y) / scale;

    if (!isDrawMode) {
      const clickedShape = areas.find(
        (area) =>
          area.isActive &&
          isPointInShape(x, y, {
            points: area.coordinates || [],
          })
      );

      if (clickedShape && clickedShape.id) {
        onAreaClick(clickedShape.id);
        return;
      }

      setIsDragging(true);
      setStartPos({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      return;
    }

    // Check for resize dots
    let foundResizeDot = false;
    areas.forEach((area) => {
      if (area.status === AVAILABLE && area.isActive) {
        const dotPositions = getPolygonDotPositions(area.coordinates || []);
        dotPositions.forEach((dot, index) => {
          if (isPointNearDot(x, y, dot.x, dot.y)) {
            setIsResizing(true);
            setResizingShape(area);
            setResizingDotIndex(index);
            foundResizeDot = true;
          }
        });
      }
    });

    if (!foundResizeDot) {
      setDrawingPoints((prev) => {
        const newPoints = [...prev, { x, y }];
        const maxPoints = getMaxPoints(shapeType);

        if (newPoints.length === maxPoints) {
          finishDrawing(newPoints);
          return [];
        }
        return newPoints;
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    const x = (pos.x - offset.x) / scale;
    const y = (pos.y - offset.y) / scale;

    if (isDrawMode) {
      let overDot = false;
      areas.forEach((area) => {
        if (area.status === AVAILABLE && area.isActive) {
          const dotPositions = getPolygonDotPositions(area.coordinates || []);
          dotPositions.forEach((dot) => {
            if (isPointNearDot(x, y, dot.x, dot.y)) {
              overDot = true;
              if (canvasRef.current) {
                canvasRef.current.style.cursor = "move";
              }
            }
          });
        }
      });

      if (!overDot && !isResizing && canvasRef.current) {
        canvasRef.current.style.cursor = "crosshair";
      }

      if (isResizing && resizingShape && resizingDotIndex !== null) {
        resizeShape(resizingShape, resizingDotIndex, x, y);
      } else {
        setCurrentMousePos({ x, y });
      }
    } else if (isDragging) {
      setOffset({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y,
      });
    } else {
      // Update cursor based on hover
      const hoveredShape = getShapeAtPosition(x, y);

      canvasRef!.current!.style.cursor = hoveredShape ? "pointer" : "default";
    }
  };

  // Get shape at given point
  const getShapeAtPosition = (x: number, y: number): any | null => {
    for (let i = areas.length - 1; i >= 0; i--) {
      if (
        isPointInShape(x, y, {
          points: areas[i].coordinates || [],
        })
      ) {
        return areas[i];
      }
    }
    return null;
  };

  const isPointInShape = (x: number, y: number, shape: any) => {
    if (!shape.points || shape.points.length < 3) return false;

    let inside = false;
    for (
      let i = 0, j = shape.points.length - 1;
      i < shape.points.length;
      j = i++
    ) {
      const xi = shape.points[i].x;
      const yi = shape.points[i].y;
      const xj = shape.points[j].x;
      const yj = shape.points[j].y;

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const resizeShape = (shape: any, dotIndex: number, x: number, y: number) => {
    // const coordinates = shape.coordinates || [];
    // if (coordinates.length === 0) return;

    // const isCorner = dotIndex % 2 === 0;
    // const pointIndex = Math.floor(dotIndex / 2);
    // const nextPointIndex = (pointIndex + 1) % coordinates.length;

    // if (isCorner) {
    //   coordinates[pointIndex].x = x;
    //   coordinates[pointIndex].y = y;
    // } else {
    //   const prevPoint = coordinates[pointIndex];
    //   const nextPoint = coordinates[nextPointIndex];
    //   const midX = (prevPoint.x + nextPoint.x) / 2;
    //   const midY = (prevPoint.y + nextPoint.y) / 2;
    //   const dx = x - midX;
    //   const dy = y - midY;
    //   coordinates[pointIndex].x += dx;
    //   coordinates[pointIndex].y += dy;
    //   coordinates[nextPointIndex].x += dx;
    //   coordinates[nextPointIndex].y += dy;
    // }

    // const updatedAreas = areas.map((area) =>
    //   area.id === shape.id ? { ...area, coordinates } : area
    // );

    // onAreasChange(updatedAreas);
    // draw();

    const coordinates = shape.coordinates || [];
    if (coordinates.length === 0) return;

    // Direct mapping - dotIndex corresponds to the corner point index
    coordinates[dotIndex].x = x;
    coordinates[dotIndex].y = y;

    const updatedAreas = areas.map((area) =>
      area.id === shape.id ? { ...area, coordinates } : area
    );

    onAreasChange(updatedAreas);
    draw();
  };

  const isPointNearDot = (
    x: number,
    y: number,
    dotX: number,
    dotY: number,
    threshold = 14
  ) => {
    const dx = x - dotX;
    const dy = y - dotY;
    return Math.sqrt(dx * dx + dy * dy) <= threshold;
  };

  const getMousePos = (
    e: React.MouseEvent<HTMLCanvasElement> | React.Touch
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const finishDrawing = (points: Array<{ x: number; y: number }>) => {
    // Generate a temporary ID for the new area
    const tempId = `new-area-${Date.now()}`;

    // Create a new area with the drawn points
    const newArea: IArea = {
      id: tempId,
      status: AVAILABLE,
      isActive: true,
      coordinates: points,
      assignedUsers: [],
      hasAcceptedRequest: false,
      activeRequestCount: 0,
      assignedActivities: [],
      name: "",
    };

    // Add the new area to the current level's areas
    onAreasChange([...areas, newArea]);

    // IMPORTANT: Add a small delay before triggering the name dialog
    // This prevents state conflicts with drawer closing animations
    setTimeout(() => {
      onAreaClick(tempId);
    }, 200);

    setDrawingPoints([]);
    // Reset drawing state
    setCurrentMousePos(null);
    draw();
  };

  const getCursorStyleForDot = () => {
    if (isResizing) return "move";
    if (isDrawMode) return "crosshair";
    if (isDragging) return "grabbing";
    return "grab";
  };

  const onWheelChange = (e: React.WheelEvent<HTMLCanvasElement>) => {
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    setScale((prevScale) => {
      const newScale = prevScale * zoomFactor;

      // Zoom limits
      if (newScale < 0.1 || newScale > 10) return prevScale;

      setOffset((prevOffset) => ({
        x: mouseX - (mouseX - prevOffset.x) * zoomFactor,
        y: mouseY - (mouseY - prevOffset.y) * zoomFactor,
      }));

      return newScale;
    });
  };

  // Add this state variable near your other state declarations
  const [initialPinchComplete, setInitialPinchComplete] = useState(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    // Always prevent default behavior to stop browser from handling the touch
    e.preventDefault();

    if (e.touches.length === 2) {
      // Handle pinch zoom start
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const touchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setLastTouchDistance(touchDistance);
      setIsZooming(true);
      setInitialPinchComplete(false); // Reset the initial pinch flag

      // Store the center position for zooming
      const midX = (touch1.clientX + touch2.clientX) / 2;
      const midY = (touch1.clientY + touch2.clientY) / 2;

      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setStartPos({
          x: midX - rect.left,
          y: midY - rect.top,
        });
      }
    } else if (e.touches.length === 1 && !isZooming) {
      const touch = e.touches[0];

      // Handle single touch for pan or interaction
      const pos = getMousePos(touch);
      const x = (pos.x - offset.x) / scale;
      const y = (pos.y - offset.y) / scale;

      if (!isDrawMode) {
        const clickedShape = areas.find(
          (area) =>
            area.isActive &&
            isPointInShape(x, y, {
              points: area.coordinates || [],
            })
        );

        if (clickedShape && clickedShape.id) {
          onAreaClick(clickedShape.id);
          return;
        }

        setIsDragging(true);
        setStartPos({
          x: touch.clientX - offset.x,
          y: touch.clientY - offset.y,
        });
        return;
      }

      // Drawing mode logic for touch
      let foundResizeDot = false;
      areas.forEach((area) => {
        if (area.status === AVAILABLE && area.isActive) {
          const dotPositions = getPolygonDotPositions(area.coordinates || []);
          dotPositions.forEach((dot, index) => {
            if (isPointNearDot(x, y, dot.x, dot.y)) {
              setIsResizing(true);
              setResizingShape(area);
              setResizingDotIndex(index);
              foundResizeDot = true;
            }
          });
        }
      });

      if (!foundResizeDot && isDrawMode) {
        setDrawingPoints((prev) => {
          const newPoints = [...prev, { x, y }];
          const maxPoints = getMaxPoints(shapeType);

          if (newPoints.length === maxPoints) {
            finishDrawing(newPoints);
            return [];
          }
          return newPoints;
        });
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    // Always prevent default to stop browser zoom
    e.preventDefault();

    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const newTouchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      if (lastTouchDistance && lastTouchDistance > 0) {
        // Calculate the relative change in distance
        const changeRatio =
          Math.abs(newTouchDistance - lastTouchDistance) / lastTouchDistance;

        // Skip tiny movements on first touch to prevent the initial jump
        if (!initialPinchComplete && changeRatio < 0.05) {
          setLastTouchDistance(newTouchDistance);
          return;
        }

        // After we've moved enough, allow normal zooming
        setInitialPinchComplete(true);

        // Calculate midpoint of the two touches
        const midX = (touch1.clientX + touch2.clientX) / 2;
        const midY = (touch1.clientY + touch2.clientY) / 2;

        // Calculate the canvas-relative position
        const rect = canvasRef.current?.getBoundingClientRect();
        if (rect) {
          const canvasMidX = midX - rect.left;
          const canvasMidY = midY - rect.top;

          // Calculate zoom ratio
          const zoomRatio = newTouchDistance / lastTouchDistance;

          // Use an even smaller smoothing factor for smoother zooming
          const smoothingFactor = initialPinchComplete ? 0.2 : 0.1;
          const oldScale = scale;

          // Calculate new scale with limits
          const newScale = Math.min(
            Math.max(oldScale * (1 + (zoomRatio - 1) * smoothingFactor), 0.1),
            15
          );

          // Adjust the offset to zoom toward touch center
          setOffset({
            x: canvasMidX - (canvasMidX - offset.x) * (newScale / oldScale),
            y: canvasMidY - (canvasMidY - offset.y) * (newScale / oldScale),
          });

          setScale(newScale);
        }
      }

      setLastTouchDistance(newTouchDistance);
    } else if (e.touches.length === 1) {
      // Only handle single touch moves if we're not currently zooming
      if (!isZooming) {
        const touch = e.touches[0];

        if (isDragging) {
          // Handle panning
          setOffset({
            x: touch.clientX - startPos.x,
            y: touch.clientY - startPos.y,
          });
        } else if (isDrawMode) {
          // Handle drawing or resizing
          const pos = getMousePos(touch);
          const x = (pos.x - offset.x) / scale;
          const y = (pos.y - offset.y) / scale;

          if (isResizing && resizingShape && resizingDotIndex !== null) {
            resizeShape(resizingShape, resizingDotIndex, x, y);
          } else {
            setCurrentMousePos({ x, y });
          }
        }
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    // If no touches remain, reset all touch states
    if (e.touches.length === 0) {
      setIsZooming(false);
      setIsDragging(false);
      setIsResizing(false);
      setResizingShape(null);
      setResizingDotIndex(null);
      setLastTouchDistance(0);
      setInitialPinchComplete(false); // Reset the initial pinch flag
    }
    // If we still have one touch but were previously zooming,
    // update for single touch interaction
    else if (e.touches.length === 1 && isZooming) {
      setIsZooming(false);
      const touch = e.touches[0];
      setStartPos({
        x: touch.clientX - offset.x,
        y: touch.clientY - offset.y,
      });
    }
  };

  // Add this function to create a passive event listener for the canvas container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // This prevents the browser's default touch actions including zooming
    const preventDefaultTouch = (e: TouchEvent) => {
      e.preventDefault();
    };

    // Add event listeners with passive: false to ensure preventDefault works
    container.addEventListener("touchstart", preventDefaultTouch, {
      passive: false,
    });
    container.addEventListener("touchmove", preventDefaultTouch, {
      passive: false,
    });
    container.addEventListener("touchend", preventDefaultTouch, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", preventDefaultTouch);
      container.removeEventListener("touchmove", preventDefaultTouch);
      container.removeEventListener("touchend", preventDefaultTouch);
    };
  }, []);

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
    if (isResizing) {
      setIsResizing(false);
      setResizingShape(null);
      setResizingDotIndex(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      style={{
        touchAction: "none", // Disable browser handling of all panning/zooming gestures
        overscrollBehavior: "none", // Prevent pull-to-refresh and browser chrome gestures
        WebkitOverflowScrolling: "touch", // For iOS smooth scrolling if needed
        WebkitTapHighlightColor: "transparent", // Remove tap highlight color
        userSelect: "none", // Prevent selection
      }}
    >
      <canvas
        ref={canvasRef}
        className={`cursor-${getCursorStyleForDot()}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={onWheelChange}
      />
    </div>
  );
}

export default CanvasEditor;

/**
 * Compute the centroid of a (non-self-intersecting) polygon.
 * @param points Array of { x, y } in order, with last point implicitly connected back to first.
 */
function computeCentroid(points: Array<{ x: number; y: number }>) {
  let areaAcc = 0;
  let cxAcc = 0;
  let cyAcc = 0;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x,
      yi = points[i].y;
    const xj = points[j].x,
      yj = points[j].y;
    const cross = xi * yj - xj * yi;
    areaAcc += cross;
    cxAcc += (xi + xj) * cross;
    cyAcc += (yi + yj) * cross;
  }
  const area = areaAcc / 2;
  if (area === 0) {
    // fallback to simple average if degenerate
    const sum = points.reduce((s, p) => ({ x: s.x + p.x, y: s.y + p.y }), {
      x: 0,
      y: 0,
    });
    return { x: sum.x / points.length, y: sum.y / points.length };
  }
  return {
    x: cxAcc / (6 * area),
    y: cyAcc / (6 * area),
  };
}

/**
 * Splits a string into lines that fit within maxWidth when drawn on ctx.
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  const words = text.split(/\s+/);

  for (let word of words) {
    // If word itself fits, try appending it to the last line
    if (ctx.measureText(word).width <= maxWidth) {
      const lastIndex = lines.length - 1;
      const testLine = lastIndex >= 0 ? lines[lastIndex] + " " + word : word;
      if (ctx.measureText(testLine).width <= maxWidth) {
        if (lastIndex >= 0) lines[lastIndex] = testLine;
        else lines.push(word);
      } else {
        lines.push(word);
      }
    } else {
      // Word is too long, break it into chunks
      let chunk = "";
      for (const char of word) {
        const testChunk = chunk + char;
        if (ctx.measureText(testChunk).width <= maxWidth) {
          chunk = testChunk;
        } else {
          // push current chunk and start a new one
          if (chunk) lines.push(chunk);
          chunk = char;
        }
      }
      if (chunk) lines.push(chunk);
    }
  }

  return lines;
}
